import intersection from 'lodash/intersection.js';
import some from 'lodash/some.js';
import { computed, getCurrentInstance, unref } from 'vue';
import { useStore } from 'vuex';

import Page from '@/models/pages/Page.js';

/**
 * Institution admin roles (Owner / Admin / Instruction Admin). Used where admin-only
 * surfaces are intentional; Administration menu visibility is permission-driven.
 */
export function userHasAdministrationNavRole(store, user) {
    if (!user) {
        return false;
    }

    if (user.is_super_admin) {
        return true;
    }

    return Boolean(store.getters['User/hasAdminRights']);
}

function gateUserCanViaGroup(gateInstance, permission) {
    if (typeof gateInstance.userCanViaGroup !== 'function') {
        return false;
    }

    return gateInstance.userCanViaGroup(permission);
}

function canSeeAdministrationPageNav(store, user, gateInstance, { includeAdminOwnerShortcut = true } = {}) {
    if (!user) {
        return false;
    }

    if (includeAdminOwnerShortcut) {
        if (store.getters['User/hasRole']('Admin') || store.getters['User/hasRole']('Owner')) {
            return true;
        }
    }

    if (!store.getters['InstitutionSettings/get']('page_enabled')) {
        return false;
    }

    const userGroupRoleIds = user.user_groups?.map(group => group.role_id) ?? [];

    return (
        intersection(userGroupRoleIds, Page.getPageAuthorizedRoleIds()).length > 0 ||
        gateInstance.allows('viewAny', 'Page')
    );
}

function institutionLevelAdministrationNavPermissionChecks(gateInstance, user, store) {
    const checks = [
        gateInstance.allows('viewAny', 'Ebook'),
        gateInstance.allows('viewAny', 'Video'),
        gateInstance.allows('viewAny', 'AttemptAuthorization'),
        gateInstance.allows('viewAny', 'EvaluationTemplate'),
        gateInstance.allows('viewAny', 'RubricBank'),
        gateInstance.allows('viewAny', 'Remediation'),
        gateInstance.allows('viewAny', 'SlideDeck'),
        gateInstance.allows('viewAny', 'User'),
        gateInstance.allows('viewAny', 'Integrations'),
        gateInstance.allows('viewAny', 'Quiz'),
        gateInstance.allows('viewAny', 'Page'),
        gateInstance.allows('viewAny', 'Group'),
        gateInstance.allows('viewAny', 'AttendanceScenario') || gateInstance.allows('configure', 'AttendanceScenario'),
        gateInstance.allows('viewAny', 'Location'),
        gateInstance.allows('viewAny', 'Report'),
        gateInstance.userCan('view the activity log'),
        gateInstance.userCan('view an institution setting'),
        gateInstance.userCan('view the impersonation log'),
        gateInstance.userCan('view risk assessment report'),
        gateInstance.userCan('view a dashboard'),
        gateInstance.userCan('view insight dashboard'),
        gateInstance.userCan("create another user's quiz attempt"),
        gateInstance.userCan('view horizon'),
        gateInstance.userCan('view pulse'),
    ];

    if (store && user) {
        checks.push(canSeeAdministrationPageNav(store, user, gateInstance, { includeAdminOwnerShortcut: false }));
    }

    return checks;
}

function groupScopedAdministrationNavPermissionChecks(gateInstance, user) {
    const checks = [
        gateUserCanViaGroup(gateInstance, 'view insight dashboard'),
        gateUserCanViaGroup(gateInstance, "create another user's quiz attempt"),
    ];

    if (user?.isGroupInstructor?.()) {
        checks.push(true);
    }

    return checks;
}

export function hasInstitutionLevelAdministrationNavPermission(gateInstance, user = null, store = null) {
    if (!gateInstance) {
        return false;
    }

    return some(institutionLevelAdministrationNavPermissionChecks(gateInstance, user, store));
}

export function hasGroupScopedAdministrationNavPermission(gateInstance, user = null) {
    if (!gateInstance) {
        return false;
    }

    return some(groupScopedAdministrationNavPermissionChecks(gateInstance, user));
}

export function hasAnyAdministrationNavPermission(gateInstance, user = null, store = null) {
    return (
        hasInstitutionLevelAdministrationNavPermission(gateInstance, user, store) ||
        hasGroupScopedAdministrationNavPermission(gateInstance, user)
    );
}

function isStudentWithOnlyGroupScopedAdminAccess(store, user) {
    if (!user) {
        return false;
    }

    if (user.is_super_admin) {
        return false;
    }

    if (store.getters['User/hasAdminRights']) {
        return false;
    }

    return store.getters['User/hasRole']('Student');
}

/**
 * Show Administration when the user would see at least one administration nav item.
 * Blocks Students who only have group-scoped permissions (no institution-level access).
 */
export function canSeeAdministrationDropdown({ store, user, gateInstance }) {
    if (!user || !gateInstance) {
        return false;
    }

    const hasInstitutionLevel = hasInstitutionLevelAdministrationNavPermission(gateInstance, user, store);
    if (hasInstitutionLevel) {
        return true;
    }

    const hasGroupScoped = hasGroupScopedAdministrationNavPermission(gateInstance, user);
    if (!hasGroupScoped) {
        return false;
    }

    if (isStudentWithOnlyGroupScopedAdminAccess(store, user)) {
        return false;
    }

    return true;
}

export function useCanSeeAdministrationDropdown({ user }) {
    const store = useStore();
    const instance = getCurrentInstance();
    const gateInstance = instance?.appContext.config.globalProperties.$gate ?? instance?.proxy?.$gate;

    return {
        canSeeAdministrationDropdown: computed(() => {
            const resolvedUser = unref(user);

            if (!resolvedUser) {
                return false;
            }

            return canSeeAdministrationDropdown({ store, user: resolvedUser, gateInstance });
        }),
    };
}
